import http from "./http";

// Multiple mounted consumers (for example, the shared header and the current
// page) can request the same plan details immediately after login. Reuse only
// the in-flight request; completed responses are not cached so an intentional
// later refresh still reaches the API.
const userActivePlanDetailsRequests = new Map();

/**
 * User-facing message from API error body (e.g. FastAPI `{ detail: "..." }`).
 * @param {object} [data]
 * @param {string} fallback
 * @returns {string}
 */
function apiErrorMessageFromResponseData(data, fallback) {
  if (!data || typeof data !== "object") {
    return fallback;
  }
  const { message, error, detail } = data;
  if (typeof message === "string" && message.trim()) return message.trim();
  if (typeof error === "string" && error.trim()) return error.trim();
  if (typeof detail === "string" && detail.trim()) return detail.trim();
  if (Array.isArray(detail) && detail.length > 0) {
    const parts = detail
      .map((item) => {
        if (typeof item === "string") return item;
        if (item && typeof item === "object" && typeof item.msg === "string") return item.msg;
        return null;
      })
      .filter(Boolean);
    if (parts.length) return parts.join(" ");
  }
  return fallback;
}

/**
 * Sign Up API
 * @param {Object} userData - User registration data
 * @param {string} userData.uname - User name / Contact name
 * @param {string} userData.companyname - Company name
 * @param {string} userData.email - Email address
 * @param {string} userData.password - Password
 * @param {string} userData.phone - Phone number
 * @param {string} userData.state - State
 * @param {string} userData.city - City (optional)
 * @param {string} userData.requirements - Requirements
 * @returns {Promise<Object>} API response
 */
export async function signUp(userData) {
  try {
    const payload = {
      uname: userData.uname || userData.contactName,
      companyname: userData.companyname || userData.companyName,
      email: userData.email,
      password: userData.password,
      phone: userData.phone || userData.mobile,
      state: userData.state,
      requirements: userData.requirements || "",
    };

    // Add city if provided
    if (userData.city) {
      payload.city = userData.city;
    }

    console.log("Sign Up API Call:", payload);
    const response = await http.post("/users/", payload);
    console.log("Sign Up API Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Sign Up Error:", error);
    if (error.response) {
      const data = error.response.data;
      throw {
        message: apiErrorMessageFromResponseData(data, "Sign up failed"),
        status: error.response.status,
        data,
      };
    }
    throw {
      message: "Network error. Please try again.",
      status: 0,
    };
  }
}

/**
 * Create User API — POST /users/ (called on Step 2 "Sign Up")
 * @param {Object} userData - Full signup payload (step 1 + step 2)
 * @returns {Promise<Object>} API response
 */
export async function createUser(userData) {
  try {
    const payload = {
      uname: userData.uname || userData.contactName || "",
      companyname: userData.companyname || userData.companyName || "",
      email: userData.email || "",
      password: userData.password || "",
      phone: (userData.phone || userData.mobile || "").replace(/\s/g, ""),
      state: userData.state?.trim() || "",
      city: userData.city?.trim() || "",
      zone: userData.zone?.trim() || "",
      requirements: userData.requirements?.trim() || "",
    };
    console.log("Create User API Call:", { ...payload, password: payload.password ? "***" : "" });
    const response = await http.post("/users/", payload);
    console.log("Create User API Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Create User Error:", error);
    if (error.response) {
      const data = error.response.data;
      throw {
        message: apiErrorMessageFromResponseData(data, "Create user failed"),
        status: error.response.status,
        data,
      };
    }
    throw {
      message: "Network error. Please try again.",
      status: 0,
    };
  }
}

/**
 * Login API
 * @param {Object} credentials - Login credentials
 * @param {string} credentials.identifier - Email address or contact number
 * @param {string} credentials.password - Password
 * @returns {Promise<Object>} API response with user data and token
 */
export async function login(credentials) {
  try {
    const payload = {
      identifier: credentials.identifier || credentials.email || credentials.contactNumber,
      password: credentials.password,
    };

    console.log("Login API Call:", payload);
    const response = await http.post("/users/login", payload);
    console.log("Login API Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Login Error:", error);
    if (error.response) {
      const data = error.response.data;
      throw {
        message: apiErrorMessageFromResponseData(data, "Login failed"),
        status: error.response.status,
        data,
      };
    }
    throw {
      message: "Network error. Please try again.",
      status: 0,
    };
  }
}

/**
 * Change password API (reset user password)
 * POST /users/{user_id}/reset-password — validates existing password, then updates to new password.
 * @param {Object} payload
 * @param {string|number} payload.userId - User ID (path parameter)
 * @param {string} payload.currentPassword - Existing password
 * @param {string} payload.newPassword - New password
 * @returns {Promise<Object>} API response
 */
export async function changePassword(payload) {
  const userId = payload.userId;
  if (userId == null || userId === "") {
    throw { message: "User ID is required to change password.", status: 0 };
  }
  try {
    const body = {
      existing_password: payload.currentPassword,
      new_password: payload.newPassword,
    };
    const response = await http.post(`/users/${userId}/reset-password`, body);
    return response.data;
  } catch (error) {
    if (error.response) {
      const data = error.response.data;
      const msg = apiErrorMessageFromResponseData(
        data,
        "Current password is incorrect or change failed."
      );
      throw { message: msg, status: error.response.status, data };
    }
    throw { message: "Network error. Please try again.", status: 0 };
  }
}

/**
 * GET /users/{userId}?minimal=true — lightweight profile for activation form pre-fill (uname, email, phone, state, city; not company name).
 * @param {string|number} userId
 * @returns {Promise<Object>}
 */
export async function fetchUserById(userId) {
  if (userId == null || userId === "") {
    throw { message: "User ID is required.", status: 0 };
  }
  const response = await http.get(`/users/${userId}`, {
    params: { minimal: true },
  });
  return response.data;
}

/**
 * Update User API (Step 2 - PUT request to update user profile)
 * PUT /users/{userId}
 * @param {string|number} userId - User ID from Step 1 response
 * @param {Object} userData - User data from step 2
 * @param {string} userData.zone - Zone (optional)
 * @param {string} userData.state - State
 * @param {string} userData.city - City
 * @param {string} userData.requirements - Requirements
 * @returns {Promise<Object>} API response
 */
export async function updateUser(userId, userData) {
  if (userId == null || userId === "") {
    throw { message: "User ID is required to update user.", status: 0 };
  }
  try {
    const payload = {
      state: userData.state?.trim() || "",
      city: userData.city?.trim() || "",
      requirements: userData.requirements?.trim() || "",
    };

    // Add zone if provided
    if (userData.zone) {
      payload.zone = userData.zone?.trim() || "";
    }

    console.log("Update User API Call:", { userId, payload });
    // Use non-trailing-slash route to avoid 307 redirect on PUT.
    const response = await http.put(`/users/${userId}`, payload);
    console.log("Update User API Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Update User Error:", error);
    if (error.response) {
      const data = error.response.data;
      throw {
        message: apiErrorMessageFromResponseData(data, "Update user failed"),
        status: error.response.status,
        data,
      };
    }
    throw {
      message: "Network error. Please try again.",
      status: 0,
    };
  }
}

/**
 * User active plan details – categories and states per plan.
 * GET /utils/user-active-plan-details/?user_id={userId}
 * Used when clicking Demo or on dashboard load; same shape can be returned by login API.
 * @param {string|number} userId
 * @returns {Promise<{ plans: Array<{ plan_id: number, selectedcategories?: string[], selectedstates?: string[] }> }>}
 */
export async function fetchUserActivePlanDetails(userId) {
  if (userId == null || userId === "") {
    throw { message: "User ID is required.", status: 0 };
  }

  const requestKey = String(userId);
  const pendingRequest = userActivePlanDetailsRequests.get(requestKey);
  if (pendingRequest) return pendingRequest;

  const request = http
    .get("/utils/user-active-plan-details/", {
      params: { user_id: userId },
    })
    .then((response) => response.data)
    .finally(() => {
      if (userActivePlanDetailsRequests.get(requestKey) === request) {
        userActivePlanDetailsRequests.delete(requestKey);
      }
    });

  userActivePlanDetailsRequests.set(requestKey, request);
  return request;
}

/**
 * @param {string} path
 * @param {string} phone
 * @returns {Promise<{ requiresOtp: boolean, sessionId: string|null, raw: object }>}
 */
async function sendOtpToPath(path, phone) {
  const mobile = String(phone || "").replace(/\D/g, "");
  const response = await http.post(path, { phone: mobile });
  const data = response?.data || {};
  return {
    requiresOtp: !!data.requires_otp,
    sessionId: data.session_id || null,
    raw: data,
  };
}

/**
 * Send OTP for public lead / inquiry forms (not strict account signup).
 * POST /users/signup/send-otp
 * @param {string} phone - 10-digit mobile (non-digits stripped)
 */
export async function sendFormOtp(phone) {
  return sendOtpToPath("/users/signup/send-otp", phone);
}

/**
 * Send OTP for strict account signup only.
 * POST /users/signup/strict/send-otp
 * @param {string} phone - 10-digit mobile (non-digits stripped)
 */
export async function sendSignupOtp(phone) {
  return sendOtpToPath("/users/signup/strict/send-otp", phone);
}

/**
 * User-facing copy when verify-otp HTTP request fails (e.g. wrong OTP → 400).
 * Avoids showing Axios strings like "Request failed with status code 400".
 * @param {unknown} error
 * @returns {string}
 */
function messageForVerifyOtpRequestError(error) {
  const res = error?.response;
  if (!res) {
    const msg = String(error?.message || "");
    if (/network/i.test(msg)) {
      return "Network error. Please check your connection and try again.";
    }
    return "Could not verify OTP. Please try again.";
  }

  const status = res.status;
  const data = res.data;

  let bodyMsg = "";
  if (typeof data === "string" && data.trim()) {
    bodyMsg = data.trim();
  } else if (data && typeof data === "object") {
    bodyMsg =
      apiErrorMessageFromResponseData(data, "") ||
      (Array.isArray(data.otp) && data.otp[0] ? String(data.otp[0]).trim() : "") ||
      (Array.isArray(data.non_field_errors) && data.non_field_errors[0]
        ? String(data.non_field_errors[0]).trim()
        : "");
  }

  const cleaned = bodyMsg.replace(/\s+/g, " ").trim();
  if (cleaned && !/^request failed with status code \d+/i.test(cleaned)) {
    return cleaned.length > 220 ? `${cleaned.slice(0, 217)}…` : cleaned;
  }

  if (status === 400 || status === 422) {
    return "Wrong OTP. Please check the code and try again.";
  }
  if (status === 404) {
    return "OTP session not found. Please tap Send OTP again.";
  }
  if (status === 410) {
    return "This OTP has expired. Please tap Send OTP again.";
  }
  if (status === 429) {
    return "Too many attempts. Please wait a moment and try again.";
  }
  return "Could not verify OTP. Please try again.";
}

/**
 * @param {string} path
 * @param {string} sessionId
 * @param {string} otp
 * @returns {Promise<{ success: boolean, message?: string, raw: object }>}
 */
async function verifyOtpAtPath(path, sessionId, otp) {
  try {
    const response = await http.post(path, {
      session_id: sessionId,
      otp: String(otp || "").trim(),
    });
    const data = response?.data || {};
    return {
      success: !!data.success,
      message: data.message,
      raw: data,
    };
  } catch (error) {
    return {
      success: false,
      message: messageForVerifyOtpRequestError(error),
      raw: error?.response?.data ?? null,
    };
  }
}

/**
 * Verify OTP for public lead / inquiry forms (not strict account signup).
 * POST /users/signup/verify-otp
 */
export async function verifyFormOtp(sessionId, otp) {
  return verifyOtpAtPath("/users/signup/verify-otp", sessionId, otp);
}

/**
 * Verify OTP for strict account signup only.
 * POST /users/signup/strict/verify-otp
 */
export async function verifySignupOtp(sessionId, otp) {
  return verifyOtpAtPath("/users/signup/strict/verify-otp", sessionId, otp);
}

/**
 * Forgot Password — Step 1: Send OTP
 * POST /users/forgot-password — verifies email exists then sends a 6-digit OTP.
 * Returns 404 if the email is not registered.
 * @param {string} identifier - User's email address
 * @returns {Promise<Object>} API response with otp_sent flag
 */
export async function forgotPasswordSendOTP(identifier) {
  try {
    const payload = {
      identifier: (identifier || "").trim(),
    };

    if (!payload.identifier) {
      throw { message: "Email address is required.", status: 0 };
    }

    console.log("Forgot Password Send OTP API Call:", { identifier: payload.identifier });
    const response = await http.post("/users/forgot-password", payload);
    console.log("Forgot Password Send OTP API Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Forgot Password Send OTP Error:", error);
    if (error.response) {
      const data = error.response.data;
      throw {
        message: apiErrorMessageFromResponseData(
          data,
          "Could not send OTP. Please try again."
        ),
        status: error.response.status,
        data,
      };
    }
    throw {
      message: error.message || "Network error. Please try again.",
      status: error.status || 0,
    };
  }
}

/**
 * Forgot Password — Step 2: Verify OTP
 * POST /users/forgot-password/verify-otp — validates the 6-digit OTP.
 * @param {string} email - Email address
 * @param {string} otp - 6-digit OTP
 * @returns {Promise<Object>} API response with verified flag
 */
export async function forgotPasswordVerifyOTP(email, otp) {
  try {
    const payload = {
      email: (email || "").trim(),
      otp: (otp || "").trim(),
    };

    if (!payload.email) {
      throw { message: "Email address is required.", status: 0 };
    }
    if (!payload.otp || payload.otp.length !== 6) {
      throw { message: "Please enter a valid 6-digit OTP.", status: 0 };
    }

    console.log("Forgot Password Verify OTP API Call:", { email: payload.email });
    const response = await http.post("/users/forgot-password/verify-otp", payload);
    console.log("Forgot Password Verify OTP API Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Forgot Password Verify OTP Error:", error);
    if (error.response) {
      const data = error.response.data;
      throw {
        message: apiErrorMessageFromResponseData(
          data,
          "OTP verification failed. Please try again."
        ),
        status: error.response.status,
        data,
      };
    }
    throw {
      message: error.message || "Network error. Please try again.",
      status: error.status || 0,
    };
  }
}

/**
 * Forgot Password — Step 3: Reset Password
 * POST /users/forgot-password/reset — re-verifies OTP and updates password.
 * @param {string} email - Email address
 * @param {string} otp - 6-digit OTP
 * @param {string} newPassword - New password
 * @param {string} confirmPassword - Confirm new password
 * @returns {Promise<Object>} API response with success flag
 */
export async function forgotPasswordReset(email, otp, newPassword, confirmPassword) {
  try {
    const payload = {
      email: (email || "").trim(),
      otp: (otp || "").trim(),
      new_password: newPassword,
      confirm_password: confirmPassword,
    };

    if (!payload.email) {
      throw { message: "Email address is required.", status: 0 };
    }
    if (!payload.otp || payload.otp.length !== 6) {
      throw { message: "Valid OTP is required.", status: 0 };
    }
    if (!payload.new_password) {
      throw { message: "New password is required.", status: 0 };
    }
    if (payload.new_password !== payload.confirm_password) {
      throw { message: "Passwords do not match.", status: 0 };
    }

    console.log("Forgot Password Reset API Call:", { email: payload.email });
    const response = await http.post("/users/forgot-password/reset", payload);
    console.log("Forgot Password Reset API Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Forgot Password Reset Error:", error);
    if (error.response) {
      const data = error.response.data;
      throw {
        message: apiErrorMessageFromResponseData(
          data,
          "Password reset failed. Please try again."
        ),
        status: error.response.status,
        data,
      };
    }
    throw {
      message: error.message || "Network error. Please try again.",
      status: error.status || 0,
    };
  }
}

/**
 * Forgot Password — Resend OTP
 * POST /users/forgot-password/resend-otp — invalidates previous OTP and sends a new one.
 * @param {string} email - Email address
 * @returns {Promise<Object>} API response with otp_sent flag
 */
export async function forgotPasswordResendOTP(email) {
  try {
    const payload = {
      email: (email || "").trim(),
    };

    if (!payload.email) {
      throw { message: "Email address is required.", status: 0 };
    }

    console.log("Forgot Password Resend OTP API Call:", { email: payload.email });
    const response = await http.post("/users/forgot-password/resend-otp", payload);
    console.log("Forgot Password Resend OTP API Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Forgot Password Resend OTP Error:", error);
    if (error.response) {
      const data = error.response.data;
      throw {
        message: apiErrorMessageFromResponseData(
          data,
          "Could not resend OTP. Please try again."
        ),
        status: error.response.status,
        data,
      };
    }
    throw {
      message: error.message || "Network error. Please try again.",
      status: error.status || 0,
    };
  }
}

// Legacy alias — kept for backward compatibility if referenced elsewhere
export const forgotPassword = forgotPasswordSendOTP;
